import type { Ticket } from './types'

// The wait a line is called by.
//
// A mirror of the server's callingWait and CallsBefore (internal/queue/calling.go),
// and not the authority. What it decides is the order a line is drawn in and the
// wait written beside each customer. Who is actually next the server says itself,
// on the desk's view (CounterView.nextTicketId), from the very selection Call next
// makes: the rules that choose on something other than the wait and priority — queue
// order, fair share, a condition — are the server's alone, and a copy of them here
// would be a second answer waiting to disagree with the first.
//
// It exists because the line used to be drawn by waitedSeconds, the reports' clock,
// which stops at the first call. A customer called, missed and put back carried
// that frozen figure for the rest of the day: the counter showed them at the bottom
// of its list while Call next took them from the top.

/**
 * Whether this ticket is sitting in a pool a desk could call from: parked, with the
 * park still written on it. The server's InPool.
 */
function inPool(ticket: Ticket): boolean {
  return ticket.state === 'parked' && Boolean(ticket.parkScope) && Boolean(ticket.parkedAt)
}

/** callingWaitMs is callingWaitSeconds before rounding, which is what ordering uses. */
function callingWaitMs(ticket: Ticket, now: number): number {
  if (inPool(ticket)) {
    // A pooled customer is called by the time since they stepped aside: somebody
    // parked two minutes ago has not been waiting an hour, however long ago they
    // arrived.
    return Math.max(0, now - Date.parse(ticket.parkedAt!))
  }
  // What earlier spells in a line banked, and the spell running now. No spell
  // recorded is read as one begun at issue, exactly as the server reads it, so a
  // ticket that has never left a line reads the same as waitedSeconds does.
  const banked = (ticket.bankedWaitSec ?? 0) * 1000
  const spellStart = Date.parse(ticket.queuedAt ?? ticket.issuedAt)
  // Signed, as the server's signedWait is: a booked customer's spell may begin at
  // their booked time, still to come, and two of them are called in the order of
  // their times. What is shown is never below nothing — see callingWaitSeconds.
  return banked + (now - spellStart)
}

/**
 * How long this customer has waited, as the calling rules judge it, in whole
 * seconds.
 *
 * For a customer in a line: the waiting banked in lines they have since left, plus
 * the time in this one. For a customer in a pool: the time since they were parked.
 *
 * Only meaningful for a customer who is waiting or pooled. For one at a desk, or
 * finished, the question is "how long did they wait to be called", and that is
 * waitedSeconds.
 */
export function callingWaitSeconds(ticket: Ticket, now: number = Date.now()): number {
  return Math.max(0, Math.round(callingWaitMs(ticket, now) / 1000))
}

const PRIORITY_WEIGHT: Record<string, number> = { vip: 30, priority: 20 }

/** The server's Priority.Weight: VIP above Priority above standard. */
function weight(ticket: Ticket): number {
  return PRIORITY_WEIGHT[ticket.priority] ?? 10
}

/** The server's queue.BookedDue: waiting, never called, and their booked time has come. */
export function bookedDue(ticket: Ticket, now: number = Date.now()): boolean {
  return (
    ticket.state === 'waiting' && Boolean(ticket.appointmentAt) && !ticket.firstCalledAt && now >= Date.parse(ticket.appointmentAt!)
  )
}

/**
 * Orders two customers the way Call next does: sent to the front first, then VIP,
 * then Priority, then — with bookedFirst — booked customers due, earliest booked
 * time first (queue.callGroups), then the longest calling wait, then whoever was
 * issued first. bookedFirst defaults to the policy's own default, on.
 *
 * The server breaks a tie in the wait on the number's turn and then the number.
 * The turn — how many times the letter had started again that day — is never sent,
 * so for one letter the issue time stands in for it; it orders the same way, since
 * a later turn was always drawn later.
 */
export function callsBefore(a: Ticket, b: Ticket, now: number = Date.now(), bookedFirst = true): number {
  if (Boolean(a.front) !== Boolean(b.front)) return a.front ? -1 : 1
  if (weight(a) !== weight(b)) return weight(b) - weight(a)
  if (bookedFirst) {
    const ad = bookedDue(a, now)
    if (ad !== bookedDue(b, now)) return ad ? -1 : 1
    if (ad) {
      const at = Date.parse(a.appointmentAt!) - Date.parse(b.appointmentAt!)
      if (at !== 0) return at
    }
  }
  const wait = callingWaitMs(b, now) - callingWaitMs(a, now)
  if (wait !== 0) return wait
  if (a.prefix === b.prefix) {
    const issued = Date.parse(a.issuedAt) - Date.parse(b.issuedAt)
    if (issued !== 0) return issued
  }
  if (a.sequence !== b.sequence) return a.sequence - b.sequence
  return a.displayNumber.localeCompare(b.displayNumber)
}

/** byCallingOrder sorts a copy of the tickets into the order callsBefore gives. */
export function byCallingOrder(tickets: readonly Ticket[], now: number = Date.now(), bookedFirst = true): Ticket[] {
  return [...tickets].sort((a, b) => callsBefore(a, b, now, bookedFirst))
}

/** How many of these customers have waited at least their queue's target. */
export function pastTarget(tickets: readonly Ticket[], now: number = Date.now()): number {
  return tickets.filter((t) => t.serviceLevelSec && callingWaitSeconds(t, now) >= t.serviceLevelSec).length
}
